import { Loader2, Play, Radar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { TargetareContacteStats } from "@/types/targetare";
import { formatDataOra, formatNumar } from "@/utils/targetareFormatting";

type DescoperirePanelProps = {
  stats: TargetareContacteStats | undefined;
  isLoading: boolean;
  programare: { inCurs: boolean; onClick: () => void };
  lot: { inCurs: boolean; onClick: () => void };
  termene: { inCurs: boolean; onClick: () => void };
};

function Valoare({ eticheta, valoare }: { eticheta: string; valoare: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {eticheta}
      </p>
      <p className="text-xl font-heading font-bold tabular-nums">{valoare}</p>
    </div>
  );
}

export function DescoperirePanel({
  stats,
  isLoading,
  programare,
  lot,
  termene,
}: DescoperirePanelProps) {
  if (isLoading || !stats) {
    return <Skeleton className="h-28" />;
  }
  const coada = stats.descoperire;
  const deProcesat = coada.de_procesat ?? 0;
  const inCurs = coada.in_curs ?? 0;
  const procesate = (coada.procesat ?? 0) + (coada.esuat ?? 0);
  const totalCoada = deProcesat + inCurs + procesate;
  const emailuriValide = stats.contacte_pe_tip_stare["email:valid"] ?? 0;
  const emailuriNevalidate =
    stats.contacte_pe_tip_stare["email:nevalidat"] ?? 0;
  const imb = stats.imbogatire ?? {};
  const imbDeProcesat = imb["termene:de_procesat"] ?? 0;
  const imbInCurs = imb["termene:in_curs"] ?? 0;
  const imbProcesate =
    (imb["termene:procesat"] ?? 0) + (imb["termene:esuat"] ?? 0);
  const imbTotal = imbDeProcesat + imbInCurs + imbProcesate;

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-6 p-4">
        <Radar className="h-6 w-6 text-ds-blue-600" />
        <Valoare
          eticheta="Firme cu email valid"
          valoare={formatNumar(stats.firme_cu_email_valid)}
        />
        <Valoare
          eticheta="Firme cu telefon"
          valoare={formatNumar(stats.firme_cu_telefon)}
        />
        <Valoare
          eticheta="Firme cu site"
          valoare={formatNumar(stats.firme_cu_site)}
        />
        <Valoare
          eticheta="Emailuri (valide / nevalidate)"
          valoare={`${formatNumar(emailuriValide)} / ${formatNumar(emailuriNevalidate)}`}
        />
        <div className="min-w-[220px]">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Descoperire pe site-uri
          </p>
          {totalCoada === 0 ? (
            <p className="text-sm text-muted-foreground">Neprogramată.</p>
          ) : (
            <>
              <p className="text-sm tabular-nums">
                {formatNumar(procesate)} / {formatNumar(totalCoada)} firme
                procesate
                {inCurs > 0 && ` · ${formatNumar(inCurs)} în lucru`} ·{" "}
                {formatNumar(stats.descoperire_site_verificat)} site-uri
                verificate
              </p>
              <p className="text-xs text-muted-foreground">
                ultima rulare {formatDataOra(stats.descoperire_ultima)}
              </p>
            </>
          )}
        </div>
        <div className="min-w-[200px]">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Îmbogățire Termene
          </p>
          {imbTotal === 0 ? (
            <p className="text-sm text-muted-foreground">Neprogramată.</p>
          ) : (
            <>
              <p className="text-sm tabular-nums">
                {formatNumar(imbProcesate)} / {formatNumar(imbTotal)} firme
                {imb["termene:esuat"]
                  ? ` · ${formatNumar(imb["termene:esuat"])} eșuate`
                  : ""}
              </p>
              <p className="text-xs text-muted-foreground">
                ultima {formatDataOra(stats.imbogatire_ultima ?? null)}
              </p>
            </>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={termene.inCurs}
            onClick={termene.onClick}
            title="Adaugă următoarele 1.200 de firme țintă (cele mai mari primele) în coada de îmbogățire Termene; o interogare per firmă"
          >
            {termene.inCurs ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Radar className="mr-2 h-4 w-4" />
            )}
            Îmbogățire Termene (+1.200)
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={programare.inCurs}
            onClick={programare.onClick}
            title="Extrage contactele din listele oficiale și pune firmele A/B/C în coada de descoperire"
          >
            {programare.inCurs ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Radar className="mr-2 h-4 w-4" />
            )}
            Programează descoperirea
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={lot.inCurs || deProcesat === 0}
            onClick={lot.onClick}
            title="Procesează acum un lot de 5 firme (restul rulează automat în fundal)"
          >
            {lot.inCurs ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Play className="mr-2 h-4 w-4" />
            )}
            Rulează un lot
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
